import { getBackendURL } from '../../../components/env';
import { bearerAuthHeaders } from '../../../utils/backendAuth';

// ── Saved Master Note diffs ────────────────────────────────────────────────
// One saved Master Note diff per user, per document, written into the files
// bucket so a note generated on the iPad can be reviewed and accepted on the
// desktop. Replaces the old "Keep for later" button, which parked the note in
// React state and lost it on reload.
//
// The backend keeps it now (backend/api/master_note_runs.py): every Master Note
// run is stored there before /api/master-note/ replies, so a reload or a closed
// tab never loses one, and the mobile app reads and writes the same file. This
// page reads and deletes it through /api/master-note/run/. The record also says
// whether a run is still going: `status` is 'running' | 'ready' | 'failed'.
//
// Layout mirrors the document's own folder, with the user as a sub-folder:
//
//   doc   New/MedADocs/MEDSTUD/2026/2026-08-17/Doe, Jane-EHR-1.json
//   diff  New/MedADocs/MEDSTUD/2026/2026-08-17/Omar Shahwan/Doe, Jane-EHR-1_diff.json
//
// The date folder is the DOCUMENT's date, not the save date (Omar 2026-08-17),
// so the saved diff always sits beside the visit it belongs to and the path is
// derivable from `documentPath` alone — lookup is one exact download, and
// re-saving upserts the same key instead of orphaning the earlier file in some
// other date folder.

export const SAVED_DIFF_VERSION = 1;

// Storage keys use `/` as the folder separator, so a display name containing
// one would silently create nested folders (or escape upward). Flatten every
// separator into `-` and collapse whitespace. A name that survives as nothing
// but dots is unusable as a folder — reject it rather than write `..`.
function sanitizeUserSegment(userName) {
  const flat = String(userName || '')
    .replace(/[\\/]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
  if (!flat) return null;
  if (/^\.+$/.test(flat)) return null;
  return flat;
}

// The folder name identifying the saving user: display name, else the email's
// local-part, else the raw user id. Mirrors useUserInfo's shape.
export function savedDiffUserSegment(userInfo) {
  const name = userInfo?.name;
  if (name && String(name).trim()) return String(name).trim();
  const email = userInfo?.email;
  if (email && String(email).trim()) {
    const local = String(email).trim().split('@')[0];
    if (local) return local;
  }
  const userId = userInfo?.userId;
  if (userId && String(userId).trim()) return String(userId).trim();
  return null;
}

// The path helpers below are what the backend mirrors to name the same file.

// Storage key for this user's saved diff on this document, or null when either
// half is missing (no doc path yet, unidentified user).
export function buildSavedDiffPath(documentPath, userName) {
  const path = String(documentPath || '').trim();
  if (!path) return null;
  const user = sanitizeUserSegment(userName);
  if (!user) return null;

  const slash = path.lastIndexOf('/');
  if (slash < 0) return null;
  const dir = path.slice(0, slash);
  const filename = path.slice(slash + 1);
  if (!filename) return null;

  const base = filename.replace(/\.json$/i, '');
  return `${dir}/${user}/${base}_diff.json`;
}

async function runRequest(method, { documentPath, schema, requestId }) {
  const params = new URLSearchParams({ documentPath, schema: schema || 'public' });
  if (requestId) params.set('requestId', requestId);
  return fetch(`${getBackendURL()}/api/master-note/run/?${params}`, {
    method,
    headers: { ...(await bearerAuthHeaders()) },
  });
}

// This user's saved Master Note for this document (a run in progress, a ready
// note, or a failed run), or null when there is none. Throws when it could not
// be read, so a caller can keep what it last showed instead of blanking it.
export async function loadSavedMasterDiff({ documentPath, schema }) {
  if (!documentPath) return null;
  const res = await runRequest('GET', { documentPath, schema });
  if (!res.ok) throw new Error(`Could not load the saved Master Note (${res.status}).`);
  const body = await res.json();
  return body && body.run && typeof body.run === 'object' ? body.run : null;
}

// Discard this user's saved Master Note (only run `requestId`'s, when given).
// Returns whether it is gone; a run still in progress is not deleted.
export async function removeSavedMasterDiff({ documentPath, schema, requestId }) {
  if (!documentPath) return false;
  try {
    const res = await runRequest('DELETE', { documentPath, schema, requestId });
    return res.ok;
  } catch (_e) {
    return false;
  }
}
